/**
 * ChatInterface - Container component for chat functionality
 * Follows ADR-002: Modularity - Composes focused hooks and components
 * Follows ADR-004: Separation of Concerns - Logic in hooks, presentation in components
 */

import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation, type Language } from '../hooks/useTranslation';
import { useChatTransport } from '../hooks/useChatTransport';
import type { PollingConfig } from '../hooks/useEventPolling';
import { useMessageProcessing } from '../hooks/useMessageProcessing';
import { useChatSession } from '../hooks/useChatSession';
import { useChatMessaging } from '../hooks/useChatMessaging';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { useScrollManagement } from '../hooks/useScrollManagement';
import { useWelcomeMessage } from '../hooks/useWelcomeMessage';
import { useChatActions } from '../hooks/useChatActions';
import { ErrorDisplay } from './ErrorDisplay';
import { MessagesList } from './MessagesList';
import { ChatInput } from './ChatInput';
import { LayoutDebugPanel } from './LayoutDebugPanel';
import { log, logError } from '../utils/logger';
import { useLayoutDebugRecorder } from '../hooks/useLayoutDebugRecorder';
import { setLayoutDebugEnabled } from '../utils/layoutDebug';
import type { AttributionType } from '../types/chat';
import type { SharedChatState, SharedChatStateSetters } from '../hooks/useSharedChatState';

/**
 * Props for the ChatInterface component
 */
export interface ChatInterfaceProps {
  /** The URL of the chat server */
  serverUrl: string;
  /** The ID of the agent to chat with */
  agentId: string;
  /** The ID of the customer */
  customerId: string;
  /** Customer name for personalization */
  customerName?: string;
  /** Agent name for display purposes */
  agentName?: string;
  /** Authentication token for backend requests */
  authToken?: string;
  /** When true, block session creation until authToken is present */
  requireAuth?: boolean;
  /** Whether to automatically scroll to new messages (default: true) */
  scrollToNewMessage?: boolean;
  /** Smart auto-scroll behavior (default: true) */
  respectUserScroll?: boolean;
  /** Language for the interface */
  language?: Language;
  /** Session ID if using existing session */
  sessionId?: string | null;
  /** Whether welcome message has been shown across chat modes */
  hasShownWelcome?: boolean;
  /** Callback when session is created */
  onSessionCreated?: (sessionId: string) => void;
  /** Callback when welcome message is shown */
  onWelcomeShown?: () => void;
  /** Custom polling intervals for adaptive polling (optional) */
  pollingConfig?: PollingConfig;
  /** Attribution type: 'none' | 'enter1nine' (default: 'none') */
  attribution?: AttributionType;
  /** Welcome messages per language (optional) */
  welcomeMessages?: Record<'da' | 'en', string>;
  /** Callback when connection status changes */
  onConnectionChange?: (isConnected: boolean, browserOffline?: boolean) => void;
  /** External input message value (for controlled state) */
  inputMessage?: string;
  /** Callback for input message changes (for controlled state) */
  onInputMessageChange?: (message: string) => void;
  /** Whether debug mode is enabled (shows draft, logs, etc.) */
  debugMode?: boolean;
  /** Records scroll/bubble/cursor layout timeline for jump debugging */
  layoutDebug?: boolean;
  /** Layout mode - affects message bubble width (popup mode allows full-width agent messages) */
  layoutMode?: 'popup' | 'fullscreen';
  /** Shared chat state from parent (for state persistence across mode switches) */
  sharedState?: SharedChatState;
  /** Setters for shared chat state */
  sharedStateSetters?: SharedChatStateSetters;
}

/**
 * Shared chat interface component that handles all chat functionality.
 * Can be used in both popup and full-screen modes.
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  serverUrl,
  agentId,
  customerId,
  customerName,
  agentName,
  authToken,
  requireAuth = false,
  scrollToNewMessage = true,
  respectUserScroll = true,
  language = 'en',
  sessionId: externalSessionId,
  hasShownWelcome: parentHasShownWelcome,
  onSessionCreated,
  onWelcomeShown,
  attribution = 'enter1nine',
  welcomeMessages,
  onConnectionChange,
  inputMessage: externalInputMessage,
  onInputMessageChange,
  debugMode = false,
  layoutDebug = false,
  layoutMode = 'fullscreen',
  sharedState,
  sharedStateSetters,
}) => {
  // Translation hook
  const { t } = useTranslation(language);
  const displayAgentName = agentName || t('agent.defaultName');

  // Scroll management
  const {
    containerRef: messagesContainerRef,
    contentRef: messagesContentRef,
    endRef: messagesEndRef,
    handleScroll,
    scrollToBottom,
    scrollToBottomIfNewMessages
  } = useScrollManagement({ scrollToNewMessage, respectUserScroll });

  useEffect(() => {
    setLayoutDebugEnabled(layoutDebug);
  }, [layoutDebug]);

  useLayoutDebugRecorder({
    enabled: layoutDebug,
    containerRef: messagesContainerRef,
    contentRef: messagesContentRef,
  });

  // Session management
  const {
    sessionId,
    isCreatingSession,
    error: sessionError,
    connectionTimedOut,
    hasPermanentError,
    createSession,
    retryConnection,
    setError: setSessionError,
    clearSession
  } = useChatSession({
    serverUrl,
    agentId,
    customerId,
    customerName,
    authToken,
    requireAuth,
    externalSessionId,
    onSessionCreated
  });

  // Message processing
  const {
    messages,
    statusMessage,
    pendingMessage,
    processEvents,
    addPendingMessage,
    clearPendingMessage,
    injectMessages
  } = useMessageProcessing({ 
    t,
    // Use shared state if provided (for mode switch persistence)
    externalMessages: sharedState?.messages,
    externalSetMessages: sharedStateSetters?.setMessages,
    externalStatusMessage: sharedState?.statusMessage,
    externalSetStatusMessage: sharedStateSetters?.setStatusMessage,
    externalPendingMessage: sharedState?.pendingMessage,
    externalSetPendingMessage: sharedStateSetters?.setPendingMessage,
    externalEvents: sharedState?.events,
    externalSetEvents: sharedStateSetters?.setEvents,
    debugMode,
  });

  // Event transport (SSE with automatic long-poll fallback)
  const {
    botStatus,
    updateLastUserMessageTime,
    triggerImmediatePoll
  } = useChatTransport({
    sessionId,
    serverUrl,
    authToken,
    onEventsReceived: processEvents,
    onError: (errorMsg: string) => {
      logError('Transport error:', errorMsg);
      if (errorMsg.includes('Session not found') || errorMsg.includes('session may have been deleted')) {
        log('Session not found - clearing session ID');
        clearSession();
        setSessionError('Session not found. Please start a new conversation.');
      } else {
        setSessionError(errorMsg);
      }
    },
    // Use shared lastOffset if provided (for mode switch persistence)
    externalLastOffset: sharedState?.lastOffset,
    externalSetLastOffset: sharedStateSetters?.setLastOffset,
  });

  // Connection status
  const { isConnected: isServerConnected, isOnline: isBrowserOnline } = useConnectionStatus({
    serverUrl,
    healthCheckInterval: 15000,
    failureThreshold: 2,
    enabled: !!sessionId && !hasPermanentError
  });

  const isConnected = hasPermanentError ? false : (sessionId ? isServerConnected : true);

  // Messaging
  const {
    inputMessage,
    setInputMessage,
    sendDirectMessage
  } = useChatMessaging({
    serverUrl,
    sessionId,
    authToken,
    createSession,
    onInjectMessages: injectMessages,
    externalInputMessage,
    onInputMessageChange
  });

  // Latches the session id whose greeting has finished animating in. Storing
  // the id (rather than a bool) means a session switch automatically
  // invalidates the latch — no reset effect needed.
  const [welcomeFinishedForSession, setWelcomeFinishedForSession] =
    useState<string | null>(null);

  // Welcome message
  const {
    showWelcomeMessage,
    handleWelcomeMessageAppear: handleWelcomeMessageAppearRaw,
    resetWelcomeState
  } = useWelcomeMessage({
    sessionId,
    messageCount: messages.length,
    isCreatingSession,
    parentHasShownWelcome,
    onWelcomeShown,
    scrollToBottom
  });

  // Wrap the welcome-appear callback so we can also latch the welcome flag
  // exactly when the greeting bubble has finished animating in.
  const handleWelcomeMessageAppear = useCallback((): void => {
    handleWelcomeMessageAppearRaw();
    if (sessionId) setWelcomeFinishedForSession(sessionId);
  }, [handleWelcomeMessageAppearRaw, sessionId]);

  // Derived: input is safe to auto-focus once either the greeting has
  // animated in for THIS session OR existing history has loaded (no greeting
  // will fire). The session-id check ensures a session switch resets
  // readiness automatically.
  const welcomeFinished = welcomeFinishedForSession === sessionId;
  const inputReady =
    welcomeFinished ||
    (!showWelcomeMessage &&
      !!sessionId &&
      !isCreatingSession &&
      messages.length > 0);

  // Chat actions
  const {
    sendMessage: sendMessageWithPending,
    handleButtonClick
  } = useChatActions({
    inputMessage,
    setInputMessage,
    sendDirectMessage,
    addPendingMessage,
    clearPendingMessage,
    updateLastUserMessageTime,
    triggerImmediatePoll,
    setSessionError,
    scrollToBottom
  });

  /**
   * Handles form submission with optimistic UI (pending message)
   */
  const handleFormSubmitWithPending = useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      sendMessageWithPending();
    },
    [sendMessageWithPending]
  );

  /**
   * Formats timestamp for display
   */
  const formatTimestamp = useCallback((timestamp: Date): string => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  /**
   * Gets the display name for a message source
   */
  const getSourceDisplayName = useCallback(
    (source: string, participantName?: string): string => {
      switch (source) {
        case 'customer':
          return customerName || t('message.you');
        case 'ai_agent':
        case 'human_agent':
          return participantName || displayAgentName;
        default:
          return source;
      }
    },
    [displayAgentName, customerName, t]
  );

  // Reset welcome state on external session change
  useEffect(() => {
    if (externalSessionId && externalSessionId !== sessionId) {
      resetWelcomeState();
    }
  }, [externalSessionId, sessionId, resetWelcomeState]);

  // Notify parent about connection status changes
  useEffect(() => {
    const browserOffline = !isBrowserOnline;
    onConnectionChange?.(isConnected, browserOffline);
  }, [isConnected, isBrowserOnline, onConnectionChange]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottomIfNewMessages(messages.length);
  }, [messages, scrollToBottomIfNewMessages]);

  // Scroll on status message - use requestAnimationFrame for smooth scroll
  useEffect(() => {
    if (scrollToNewMessage && statusMessage) {
      requestAnimationFrame(() => scrollToBottom());
    }
  }, [statusMessage, scrollToNewMessage, scrollToBottom]);

  const showInfo = botStatus === 'processing' || botStatus === 'typing' || statusMessage !== null;
  const authRequired = requireAuth && !authToken;

  return (
    <div className="chatbot-messages-container flex flex-col h-full">
      <LayoutDebugPanel enabled={layoutDebug} />
      {/* Auth required */}
      {authRequired && (
        <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground">
          <p>{t('status.authRequired')}</p>
        </div>
      )}
      {/* Error Display */}
      {!authRequired && sessionError && (
        <ErrorDisplay 
          message={sessionError} 
          onDismiss={() => setSessionError('')}
          onRetry={connectionTimedOut || hasPermanentError ? retryConnection : undefined}
          retryLabel={t('common.retry')}
        />
      )}

      {/* Messages Area */}
      {!authRequired && (
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="chatbot-messages-scroll-area flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 bg-background scrollbar-thin scrollbar-thumb-muted scrollbar-track-background"
      >
        <div ref={messagesContentRef}>
          <MessagesList
          messages={messages}
          pendingMessage={pendingMessage}
          statusMessage={statusMessage}
          isCreatingSession={isCreatingSession}
          showWelcomeMessage={showWelcomeMessage}
          agentName={displayAgentName}
          customerName={customerName}
          language={language}
          onWelcomeMessageAppear={handleWelcomeMessageAppear}
          hasShownWelcome={parentHasShownWelcome ?? false}
          welcomeMessages={welcomeMessages}
          getSourceDisplayName={getSourceDisplayName}
          formatTimestamp={formatTimestamp}
          t={t}
          onButtonClick={handleButtonClick}
          debugMode={debugMode}
          layoutMode={layoutMode}
        />
        </div>
        <div ref={messagesEndRef} />
      </div>
      )}

      {/* Input Area */}
      {!authRequired && (
      <ChatInput
        value={inputMessage}
        onChange={setInputMessage}
        onSubmit={handleFormSubmitWithPending}
        placeholder={
          !isConnected 
            ? t('status.connectionLost') 
            : sessionId 
              ? t('input.placeholder')
              : isCreatingSession
                ? t('input.placeholderConnecting')
                : t('input.placeholder')
        }
        disabled={!sessionId || isCreatingSession || !isConnected}
        isLoading={showInfo}
        attribution={attribution}
        autoFocusWhenReady={inputReady}
      />
      )}
    </div>
  );
};
